import { Constants, HelpItemType, HelpView, TileType } from "../constants";
import { HelpArticleItemListType, HelpArticleItemType } from "../help-article/help-article-constants";
import { normalizeArticles } from "./normalize-helper";
import { getFieldTypeNames, getHomeTiles, getOverloads, getSyntax } from "./popdock-script";

function getSelectedItemMarkdown(selectedItem, view) {
    const markdownFn = getSelectedItemMarkdownFunction(selectedItem, view);
    if (typeof markdownFn === "function") {
        return markdownFn();
    }
    return null;
}

function getSelectedItemMarkdownFunction(selectedItem, view) {
    switch (selectedItem.Type) {
        case HelpItemType.Article:
            return () => getSelectedArticleMarkdown(selectedItem, view);
        case HelpItemType.Category:
            return () => getTilePageMarkdown(selectedItem.Name, selectedItem.Description, selectedItem.Functions, selectedItem.LongDescription, view);
        case HelpItemType.Function:
            return () => getPopdockScriptFunctionMarkdown(selectedItem);
        default:
            return null;
    }
}

function getSelectedArticleMarkdown(selectedArticle, view) {
    const { Title: title, Description: description, Articles: articles, Items: items, RelatedArticles: related } = selectedArticle;
    return getArticleMarkdown(title, description, articles, items, related, view);
}

function getArticleMarkdown(title, description, articles, items, related, view) {
    let markdown = `# ${title}\n\n`;
        if (description) markdown += `${description}\n\n`;
        markdown += getArticleItemMarkdown(items);
        markdown += getTileMarkdown(normalizeArticles(articles), view);
        if (related?.length > 0) {
            markdown += "## See also\n\n";
            markdown += getTileMarkdown(normalizeArticles(related));
        }
        return markdown;
}

function getArticleItemMarkdown(items) {
    if (!items || items.length == 0)
        return "";

    let markdown = "";
    let number = 0;

    for (let i = 0, len = items.length; i < len; i++) {
        switch (items[i].type) {
            case HelpArticleItemType.Title:
                markdown += `## ${items[i].value}\n\n`;
                break;
            case HelpArticleItemType.Subtitle:
                markdown += `### ${items[i].value}\n\n`;
                break;
            case HelpArticleItemType.Text:
                markdown += `${items[i].value}\n\n`;
                break;
            case HelpArticleItemType.ListItem:
                if (items[i].listType === HelpArticleItemListType.Number) {
                    if (items[i].startOfList) {
                        number = 1;
                    } else {
                        number++;
                    }
                    markdown += `${number}. ${items[i].value}\n\n`;
                } else {
                    markdown += `- ${items[i].value}\n\n`;
                }
                break;
            case HelpArticleItemType.Tip:
                markdown += `> ${items[i].value}\n\n`;
                break;
            case HelpArticleItemType.Code:
                markdown += `\`\`\`${items[i].codeType}\n${items[i].value}\n\`\`\`\n\n`;
                break;
        }
    }

    return markdown;
}

function getPopdockScriptFunctionMarkdown(func) {
    let markdown = `# ${func.Name}\n\n`;
    if (func.Description) markdown += `${func.Description}\n\n`;
    if (func.LongDescription) markdown += `${func.LongDescription}\n\n`;

    const requiredArgs = func.Arguments.filter(arg => !arg.Optional && !arg.UiOnly);
    const optionalArgs = func.Arguments.filter(arg => arg.Optional && !arg.UiOnly);
    const overloads = getOverloads(optionalArgs);

    const syntax = overloads.map(overload => {
        return getSyntax(func.Name, requiredArgs, overload)
    }).join("\n\n");

    if (syntax) markdown += `## Syntax\n\n${syntax}\n\n`;

    const funcArgs = func.Arguments.filter(arg => !arg.UiOnly).map(arg => {
        let argMarkdown = `### ${arg.Name}\n\n`;
        if (arg.Description) argMarkdown += `${arg.Description}\n\n`;

        const fieldTypes = getFieldTypeNames(arg.FieldTypes);
        if (fieldTypes?.length > 0) argMarkdown += `#### Field types:\n\n${fieldTypes.map(fieldType => `- ${fieldType}`).join("\n")}\n\n`;

        if (arg.ListItems?.length > 0) {
            const listItems = arg.ListItems.map(listItem => {
                const description = listItem.Description ? ` :${listItem.Description}` : "";
                return `- ${listItem.Value}${description}`;
            }).join("\n");

            if (listItems) argMarkdown += `#### Valid values:\n\n${listItems}`;
        }
        return argMarkdown
    }).join("\n\n");

    if (funcArgs) markdown += `## Arguments\n\n${funcArgs}`;

    const examples = func.Examples.map(example => {
        let exampleMarkdown = `### ${example.Name}\n\n`;
        if (example.Description) exampleMarkdown += `${example.Description}\n\n`;
        return exampleMarkdown + `#### Function:\n\n${example.Function}\n\n#### Result:\n\n${example.Result}`;
    }).join("\n\n");

    if (examples) markdown += `## Examples\n\n${examples}\n\n`;

    const relatedItems = func.Related.map(relatedItem => `- ${relatedItem.Name}`).join("\n");
    if (relatedItems) markdown += `## Related items\n\n${relatedItems}`;
    return markdown;
}

function getTilePageMarkdown(title, description, tiles, longDescription, view) {
    let markdown = `# ${title}\n\n`;
    if (description) markdown += `${description}\n\n`;
    if (longDescription) markdown += `${longDescription}\n\n`;
    markdown += getTileMarkdown(tiles, view);
    return markdown;
}

function getTileMarkdown(tiles, view) {
    if (!tiles || tiles.length == 0)
        return "";

    let markdown = "";

    for (const tile of tiles) {
        if (!tile.Name) continue;
        if (tile.Type == TileType.Subtitle) {
            markdown += `## ${tile.Name}\n\n`;
        } else {
            const url = view && (tile.Slug || tile.Id) ? ` (${Constants.DocumentationBaseUrl}${view}/${tile.Slug ?? tile.Id}.md)` : ""; //todo: should fix this and just have Slug messed up introducing Id
            markdown += `### ${tile.Name}${url}\n\n`;
            if (tile.Description) markdown += `${tile.Description}\n\n`;
        }
    }

    return markdown;
}

function getTopLevelPopdockScriptPageMarkdownFunction(articles, functions) {
    return () => getTilePageMarkdown(Constants.PopScriptPageTitle, Constants.PopScriptPageDescription, getHomeTiles(articles, functions), null, HelpView.PopdockScript)
}

export { 
    getSelectedItemMarkdown, 
    getSelectedItemMarkdownFunction,
    getSelectedArticleMarkdown, 
    getArticleMarkdown, 
    getPopdockScriptFunctionMarkdown, 
    getTilePageMarkdown, 
    getTopLevelPopdockScriptPageMarkdownFunction 
}